// Plain-language glossary — ONE source of truth for the load-bearing finance
// terms used across the dashboard. Definitions are written for non-finance board
// members (firefighters / volunteers, not accountants): short, identical
// everywhere, and explanatory rather than technical.
//
// Used by <InfoTip term="…" /> (the hover/tap popover next to a jargon term).
// Keep each definition to one or two plain sentences. NO numbers — these are
// definitions, not data.

export interface GlossaryEntry {
  /** The short display label for the term (Title Case). */
  label: string;
  /** Plain-language definition. One or two sentences, no jargon, no numbers. */
  definition: string;
}

export const GLOSSARY = {
  encumbrance: {
    label: "Encumbrance",
    definition:
      "Money promised on purchase orders — committed, not yet paid. It's spoken for, so it isn't available to spend on something else.",
  },
  obligation: {
    label: "Obligation",
    definition:
      "Money the county has formally committed to pay — a step past a basic commitment, on its way to becoming an actual payment.",
  },
  commitment: {
    label: "Commitment",
    definition:
      "The earliest stage of spoken-for money — a requisition or intent to buy, before it becomes a firm obligation.",
  },
  funds_available: {
    label: "Funds Available",
    definition:
      "Cash we can still spend — what's left of the budget after subtracting what's been spent and what's promised on purchase orders.",
  },
  carryover: {
    label: "Carryover",
    definition:
      "Left over from a prior year — a purchase order opened in an earlier fiscal year that's still open now.",
  },
  object: {
    label: "Object",
    definition:
      "A spending category, or line — the type of thing the money is for (fuel, gear, repairs, and so on). The county uses a numbered code for each.",
  },
  appropriation: {
    label: "Appropriation",
    definition:
      "The budget the county sets aside for us for the year — our authorized amount to spend.",
  },
  variance: {
    label: "Variance",
    definition:
      "The gap between budget and reality. Over budget shows red; under budget shows green.",
  },
  oca: {
    label: "OCA",
    definition:
      "Organization Cost Account — the county's bucket that groups our spending. Roughly, which part of the department a cost belongs to.",
  },
  budgetary_control: {
    label: "Budgetary Control",
    definition:
      "The county's official accounting that keeps spending inside the budget. The figures here that say 'ties to Oracle' come from it.",
  },
  fiscal_year: {
    label: "Fiscal Year (FY)",
    definition:
      "Our budget year. It runs July 1 to June 30, and is named for the year it ends — FY27 ends June 30, 2027.",
  },
  fy27: {
    label: "FY27",
    definition:
      "The fiscal year ending June 30, 2027. Our budget year runs July 1 to June 30 and is named for the year it ends.",
  },
  "501c": {
    label: "501(c)(4)",
    definition:
      "Our non-profit side — a 501(c)(4) social-welfare organization (not a 501(c)(3) charity), with its own books and bank account, kept entirely separate from the county levy money.",
  },
  reconciliation: {
    label: "Reconciliation",
    definition:
      "Checking the bank statement matches our books — confirming every dollar lines up.",
  },
  bcea: {
    label: "BCEA",
    definition:
      "Budgetary Control – Expenditure Analysis, a county report. The expenditure-only view of what's left, before revenue effects are netted in.",
  },
  capital_outlay: {
    label: "Capital Outlay",
    definition:
      "Big one-time purchases like apparatus, vehicles, or major equipment — budgeted separately from everyday operating spending.",
  },
  appropriation_levy: {
    label: "Levy",
    definition:
      "The county tax money appropriated to the department (fund 2101-65-6560 in FY26, becoming 2119-65-6560 in FY27) — separate from our nonprofit funds.",
  },
  restricted_funds: {
    label: "Restricted funds",
    definition:
      "Money set aside for a specific purpose that can't be spent freely. The 1552 Payroll account is restricted, so it's excluded from the cash we count as available.",
  },
  bridge: {
    label: "Bridge",
    definition:
      "A step-by-step build-up that starts from our recurring costs, adds the changes the county approved, and arrives at next year's total operating need.",
  },
  operating_need: {
    label: "Operating need",
    definition:
      "What it costs to run the department for a year — the recurring operating budget, before one-time capital items.",
  },
  rr_shift: {
    label: "R&R shift",
    definition:
      "The Recruitment & Retention coordinator is paid from an existing employee-subsidy pot we already have — not a brand-new budget line, so it's a shift, not an add.",
  },
  passthrough: {
    label: "Pass-through",
    definition:
      "County money that flows in only to pay a specific bill and then gets reimbursed — it isn't the department's own money, so we don't count it as our income or spending.",
  },
  carryforward: {
    label: "Carryforward",
    definition:
      "Keeping a still-open purchase order alive into the new fiscal year so the remaining commitment isn't lost when the year rolls over.",
  },
  uncovered: {
    label: "Uncovered",
    definition:
      "Billing that has run above what the purchase order set aside — spend with no remaining PO coverage behind it, so it has to be funded another way.",
  },
  po_coverage: {
    label: "PO coverage",
    definition:
      "The amount a purchase order set aside to cover a vendor. Once invoices exceed it, the extra is 'uncovered' and needs a budget source.",
  },
  recovery_revenue: {
    label: "Recovery revenue",
    definition:
      "County money we've budgeted for but haven't collected yet. Until it comes in, it reduces what we can actually spend.",
  },
  net_operating: {
    label: "Own-funded net",
    definition:
      "Our own-source income minus our own-funded spending. The county reimbursement washes through and is left out, so this shows the non-profit's real operating result.",
  },
  oracle_mobius: {
    label: "Oracle / Mobius",
    definition:
      "The county's accounting system for the levy. When a figure here says it 'ties to Oracle,' it matches the county's official books.",
  },
  pcard: {
    label: "P-Card",
    definition:
      "The county purchasing credit card. Charges are real spending that must be accounted for even though they don't go through a purchase order.",
  },
  discretionary: {
    label: "Discretionary Available",
    definition:
      "A narrower slice of Funds Available — expenditure objects only, revenue excluded. Not the same number; the headline Funds Available on the Overview page is the one that controls.",
  },
} as const satisfies Record<string, GlossaryEntry>;

export type GlossaryTerm = keyof typeof GLOSSARY;

/** Look up a glossary entry by term key. Returns null for unknown keys. */
export function glossaryEntry(term: GlossaryTerm): GlossaryEntry {
  return GLOSSARY[term];
}
